import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Grid,
  Chip,
  Button,
} from "@mui/material";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function CompressorVsLimiterLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const panelBg = "rgba(255,255,255,0.04)";
  const darkPanel = "rgba(0,0,0,0.18)";

  const articleSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Compressor vs Limiter: What Is the Difference?",
      description:
        "Learn the difference between a compressor and limiter, how each controls dynamics, when to use compression, when to use limiting, and how they work together in mixing and mastering.",
      author: {
        "@type": "Organization",
        name: "Parish Audio",
      },
      publisher: {
        "@type": "Organization",
        name: "Parish Audio",
        logo: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/assets/logo.png",
        },
      },
      mainEntityOfPage: "https://parishaudio.com/learning/compressor-vs-limiter",
      articleSection: "Audio Education",
      keywords: [
        "compressor vs limiter",
        "difference between compressor and limiter",
        "compression vs limiting",
        "when to use a compressor",
        "when to use a limiter",
        "limiter vs compressor mastering",
        "audio dynamics processors",
      ],
    }),
    []
  );

  const quickFacts = [
    {
      title: "What they share",
      text: "Both compressors and limiters are dynamics processors that reduce level once a signal crosses a threshold.",
      icon: <CompareArrowsRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Main difference",
      text: "A compressor usually works more gradually and flexibly, while a limiter is more aggressive and focused on strict peak control.",
      icon: <SpeedRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where they are used",
      text: "Vocals, drums, bass, guitars, buses, mastering chains, broadcast audio, and nearly every modern production workflow.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const comparisonPoints = [
    {
      name: "Compression",
      description:
        "Compression is usually used to shape dynamic range in a more controlled, musical, and flexible way. It can smooth a vocal, glue a drum bus, thicken a bass, or help a mix feel more cohesive.",
    },
    {
      name: "Limiting",
      description:
        "Limiting is usually used when stronger peak control is needed. It is especially common at the end of a chain where the goal is to stop overs, control transients, or set a hard output ceiling.",
    },
    {
      name: "Ratio Behavior",
      description:
        "Compressors often use moderate ratios, while limiters generally use very high ratios so signals above threshold are held down much more strictly.",
    },
    {
      name: "Typical Result",
      description:
        "Compression often changes feel, density, and movement. Limiting often changes maximum level, peak behavior, and final loudness more directly.",
    },
  ];

  const controlDifferences = [
    {
      name: "Threshold",
      description:
        "Both tools use threshold, but with a limiter the threshold often leads to more immediate and more obvious peak restraint.",
    },
    {
      name: "Ratio",
      description:
        "Compressors may use gentle or moderate ratios such as 2:1, 4:1, or 8:1. Limiters usually operate at much higher ratios, sometimes effectively acting like a ceiling.",
    },
    {
      name: "Attack and Release",
      description:
        "Compressors often use attack and release for groove, punch, and musical movement. Limiters often use faster behavior to catch peaks more strictly.",
    },
    {
      name: "Ceiling / Output",
      description:
        "Limiters often include an explicit output ceiling to stop the signal from going above a set level, especially in mastering.",
    },
  ];

  const compressorUses = [
    "Smoothing out a vocal performance",
    "Adding consistency to bass guitar",
    "Giving drums more glue or sustain",
    "Controlling dynamic swings on acoustic instruments",
    "Making a bus feel tighter and more cohesive",
    "Shaping groove and movement with attack and release timing",
  ];

  const limiterUses = [
    "Catching peaks on a mix bus or master bus",
    "Preventing digital clipping",
    "Raising final loudness in mastering",
    "Controlling occasional transient spikes",
    "Protecting broadcast or live audio chains from overload",
    "Setting a strict output ceiling before export",
  ];

  const bestPractices = [
    "Use a compressor when you want to shape feel, movement, consistency, or sustain more musically.",
    "Use a limiter when you need stronger peak control or want to keep the output under a defined ceiling.",
    "Do not use a limiter to fix a mix that really needs balancing, EQ, or compression earlier in the chain.",
    "Watch for over-processing. Too much compression can kill life, and too much limiting can kill punch.",
    "Often the best result is using both: compression first for control and tone, then limiting later for final peak management.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Learn how compressors reduce dynamic range and shape tone, movement, and consistency.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "Explore how limiters control peaks, prevent clipping, and manage final loudness.",
    },
    {
      title: "Mastering Basics",
      to: "/learning/mastering-basics",
      description:
        "See how compressors and limiters often work together in a mastering chain.",
    },
    {
      title: "Transient Shaper",
      to: "/learning/transient-shaper",
      description:
        "Understand how transient shaping differs from both compression and limiting when the goal is punch and envelope control.",
    },
  ];

  const sectionCardSx = {
    p: 2,
    borderRadius: 3,
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <>
      <Seo
        title="Compressor vs Limiter | Parish Audio Learning"
        description="Learn the difference between a compressor and limiter, how each controls dynamics, when to use compression, when to use limiting, and how they work together in mixing and mastering."
        canonical="https://parishaudio.com/learning/compressor-vs-limiter"
        schema={articleSchema}
      />

      <Box ref={topContainer}
        sx={{
          width: "95%",
          maxWidth: 1100,
          mx: "auto",
          minHeight: height - 140,
        }}
      >
        <Paper
          sx={{
            mt: 3,
            mb: 4,
            p: { xs: 2, md: 3 },
            background: `linear-gradient(180deg, ${style.color1} 0%, rgba(22,32,48,0.98) 100%)`,
            borderRadius: 3,
            color: style.color4,
            boxShadow: 12,
            overflow: "hidden",
          }}
        >
          <Stack spacing={3.25}>
            <Box>
              <Chip
                label="Learning Center"
                sx={{
                  mb: 1.5,
                  color: style.color4,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  mb: 1.25,
                  color: style.color4,
                  maxWidth: 900,
                }}
              >
                Compressor vs Limiter: What Is the Difference?
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  maxWidth: 950,
                }}
              >
                Compressors and limiters belong to the same general family of
                audio processors. Both are designed to control dynamics by
                reducing level when a signal goes above a threshold. Because of
                that, people often describe a limiter as a more extreme form of
                compression.
              </Typography>

              <Typography
                sx={{
                  color: style.color3,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.85,
                  mt: 2,
                  maxWidth: 950,
                }}
              >
                That description is useful, but it is not the whole story.
                Compressors and limiters are often chosen for different jobs.
                Compressors are usually used for musical control, tone shaping,
                movement, and consistency. Limiters are usually used for strict
                peak control, preventing clipping, and setting final loudness.
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="stretch">
              {quickFacts.map((item) => (
                <Grid item xs={12} md={4} key={item.title} sx={{ display: "flex" }}>
                  <Paper
                    sx={{
                      ...sectionCardSx,
                      backgroundColor: panelBg,
                      boxShadow: 6,
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Box sx={{ flexShrink: 0 }}>{item.icon}</Box>
                      <Box>
                        <Typography
                          sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: style.color3,
                            lineHeight: 1.7,
                            fontSize: "0.97rem",
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                What compressors and limiters have in common
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                Both processors react when a signal crosses a threshold. Once
                that happens, they reduce gain. This lowers dynamic range and
                helps manage how loud the signal becomes. Both can influence
                punch, consistency, density, and perceived loudness.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85, mb: 2 }}>
                In many plugin lineups, you will see similar controls on both
                types of processors. Threshold, attack, release, output level,
                and gain reduction meters are all common. This overlap is one
                reason the two are easy to confuse at first.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                The most important difference is not whether they reduce gain.
                Both do that. The difference is how strongly they do it and what
                role they usually play in a signal chain.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                The main difference
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {comparisonPoints.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Typography
                        sx={{ color: accent, fontWeight: 700, mb: 0.75 }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{ color: style.color3, lineHeight: 1.75 }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                A good way to think about it is this: a compressor usually helps
                shape the behavior of the signal, while a limiter usually helps
                stop the signal from going too far.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                How their controls tend to differ
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {controlDifferences.map((item) => (
                  <Grid item xs={12} md={6} key={item.name} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        boxShadow: 4,
                      }}
                    >
                      <Typography
                        sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{ color: style.color3, lineHeight: 1.75 }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                When to use a compressor
              </Typography>

              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  backgroundColor: panelBg,
                }}
              >
                <Stack spacing={1.35}>
                  {compressorUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <CompressRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                Compression is often the better choice when you want to shape
                how a source behaves over time. It can smooth level changes,
                control sustain, add density, and even influence groove depending
                on how attack and release are set.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                When to use a limiter
              </Typography>

              <Paper
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  backgroundColor: panelBg,
                }}
              >
                <Stack spacing={1.35}>
                  {limiterUses.map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <SpeedRoundedIcon
                        sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Typography
                sx={{
                  color: style.color3,
                  lineHeight: 1.85,
                  mt: 2,
                }}
              >
                Limiting is usually the better choice when you need stronger
                control over maximum level. It is especially useful near the end
                of a chain, where preventing clipping or managing final loudness
                becomes more important than shaping musical dynamics in a subtle
                way.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Do they replace each other?
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                      Usually no
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In some cases, a limiter can be used where a compressor might
                    otherwise go, or a compressor can be set aggressively enough
                    to approach limiter-like behavior. But in real-world
                    workflows, they often serve different purposes rather than
                    replacing each other.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A common approach is to use compression earlier for control,
                    tone, and consistency, then use limiting later for final peak
                    management. That combination is especially common on buses and
                    mastering chains.
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Common mistakes
              </Typography>

              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: darkPanel,
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Stack spacing={1.35}>
                  {[
                    "Using a limiter when the real goal is musical consistency, which a compressor often handles better.",
                    "Using compression too heavily when a simple peak-catching limiter would solve the problem more cleanly.",
                    "Trying to get competitive loudness with only compression instead of using compression and limiting together appropriately.",
                    "Over-processing until the track loses punch, movement, and natural energy.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                      <WarningAmberRoundedIcon
                        sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                      />
                      <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Best practices
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {bestPractices.map((item, index) => (
                  <Grid item xs={12} md={6} key={index} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: darkPanel,
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <TipsAndUpdatesRoundedIcon
                          sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                        />
                        <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                          {item}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Related learning topics
              </Typography>

              <Grid container spacing={2} alignItems="stretch">
                {relatedLinks.map((item) => (
                  <Grid item xs={12} md={6} key={item.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        ...sectionCardSx,
                        backgroundColor: panelBg,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                        >
                          {item.description}
                        </Typography>
                      </Box>

                      <Box>
                        <Button
                          component={RouterLink}
                          to={item.to}
                          variant="outlined"
                          sx={{
                            color: style.color4,
                            borderColor: "rgba(255,255,255,0.18)",
                            "&:hover": {
                              borderColor: accent,
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          Read More
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 800,
                  mb: 1.5,
                  color: style.color4,
                }}
              >
                Final thoughts
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9, mb: 2 }}>
                Compressors and limiters are closely related, but they are not
                the same tool with different labels. Compression is usually the
                more flexible and musical tool for shaping consistency, movement,
                and feel. Limiting is usually the stricter tool for controlling
                peaks and setting boundaries.
              </Typography>

              <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                The best choice depends on what problem you are trying to solve.
                In many cases, the strongest workflow is not compressor or
                limiter. It is compressor and limiter, each doing the job it is
                best suited for.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </>
  );
}